/**
 * ARVEXIS BRAND COMPONENTS
 * ========================
 * 
 * Canonical source of truth for all Arvexis brand assets.
 * Implements Founder Brand Doctrine v1.1 (RC1)
 * 
 * TAGLINE: "Understand yourself. Gently."
 * 
 * USAGE RULES:
 * 
 * PRIMARY LOGO (Full Lockup - Symbol + ARVEXIS + Tagline)
 * Use whenever there is enough room:
 * - Splash screen, Home screen, Login, Sign Up
 * - Welcome screens, Hamburger Menu header
 * - Reflection loading experience
 * - Insights, Your Journey, Wellness
 * - Settings, About, Empty states
 * - Premium onboarding, Any major branded screen
 * 
 * SECONDARY LOGO (Symbol Only)
 * Use only when space is limited or subtle brand mark is appropriate:
 * - Profile avatar, Journal/PDF footer
 * - Reflection report watermark, Loading spinner
 * - Small loading indicators, Achievement badges
 * - Notification icons, Small navigation elements
 * - App icon, Favicon
 * 
 * BRAND PRINCIPLE:
 * "Does the user have enough room to appreciate the full brand?"
 * If yes → use the full logo.
 * If no → use only the symbol.
 */

import React from 'react';

// =============================================================================
// SIZE CONSTANTS
// =============================================================================

export const SYMBOL_SIZES = {
  XS: 24,   // Inline text, small indicators
  SM: 32,   // Navigation bar, compact headers
  MD: 48,   // Standard usage
  LG: 64,   // Profile avatar, cards
  XL: 80,   // Hero sections, loading screens
  XXL: 120, // Full-screen loading, PDF covers
  AVATAR: 56, // Profile avatar specifically
};

export const LOCKUP_SIZES = {
  SM: 180,   // Compact contexts
  MD: 220,   // Standard usage
  LG: 280,   // Splash, marketing
  XL: 340,   // Large marketing contexts
  HOME: 242, // Home screen specific - 10% larger than standard (elegant, not dominating)
};

// =============================================================================
// PRIMARY LOGO - FULL LOCKUP (Symbol + ARVEXIS + Tagline)
// =============================================================================

/**
 * ArvexisLockup - Full brand identity with symbol, wordmark, and tagline
 * 
 * USE FOR (spacious contexts):
 * - Home screen (primary position)
 * - Splash / Welcome / Login screens
 * - Reflection loading sequence
 * - Empty states
 * - Settings, About, Marketing
 * 
 * @param {number} width - Width in pixels (height auto-scales, maintains aspect ratio)
 * @param {string} className - Additional CSS classes
 */
export const ArvexisLockup = ({ 
  width = LOCKUP_SIZES.MD, 
  className = '' 
}) => (
  <img
    src="/assets/brand/arvexis-lockup.png"
    alt="Arvexis - Understand yourself. Gently."
    className={`arvexis-lockup ${className}`}
    style={{ 
      width: width, 
      height: 'auto',
      objectFit: 'contain'
    }}
    data-testid="arvexis-lockup"
  />
);

// =============================================================================
// SECONDARY LOGO - SYMBOL ONLY
// =============================================================================

/**
 * ArvexisSymbol - The brand mark symbol only
 * 
 * USE FOR (space-constrained contexts):
 * - Profile avatars, Small nav icons
 * - PDF footers, Loading spinners
 * - Badges, Push notifications
 * - Favicon, App icon
 * 
 * @param {number} size - Width/height in pixels (default: 48)
 * @param {string} className - Additional CSS classes
 * @param {boolean} breathing - Enable subtle breathing animation
 * @param {boolean} glow - Add golden glow effect
 * @param {string} alt - Alt text for accessibility
 */
export const ArvexisSymbol = ({ 
  size = SYMBOL_SIZES.MD, 
  className = '', 
  breathing = false,
  glow = false,
  alt = 'Arvexis'
}) => {
  const baseStyle = {
    width: size,
    height: size,
    objectFit: 'contain',
  };
  
  const glowStyle = glow ? {
    filter: 'drop-shadow(0 0 12px rgba(212, 175, 55, 0.4)) drop-shadow(0 0 24px rgba(212, 175, 55, 0.2))'
  } : {};

  return (
    <img
      src="/assets/brand/arvexis-symbol.png"
      alt={alt}
      className={`arvexis-symbol ${breathing ? 'arvexis-symbol--breathing' : ''} ${className}`}
      style={{ ...baseStyle, ...glowStyle }}
      data-testid="arvexis-symbol"
    />
  );
};

// =============================================================================
// AVATAR WRAPPER (Uses Symbol in circular container)
// =============================================================================

/**
 * ArvexisAvatar - Profile avatar with brand symbol
 * 
 * @param {number} size - Avatar size in pixels
 * @param {string} customSrc - Override with custom avatar URL (future)
 * @param {string} userName - User name for alt text
 * @param {string} className - Additional CSS classes
 */
export const ArvexisAvatar = ({ 
  size = SYMBOL_SIZES.AVATAR,
  customSrc = null,
  userName = 'User',
  className = ''
}) => {
  const src = customSrc || '/assets/brand/arvexis-symbol.png';
  const isDefault = !customSrc;

  return (
    <div 
      className={`arvexis-avatar ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDefault 
          ? 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%)'
          : 'transparent',
        border: isDefault 
          ? '2px solid rgba(212, 175, 55, 0.3)'
          : '2px solid rgba(255, 255, 255, 0.1)',
        boxShadow: isDefault 
          ? '0 0 20px rgba(212, 175, 55, 0.15)'
          : 'none',
      }}
      data-testid="arvexis-avatar"
    >
      <img
        src={src}
        alt={isDefault ? 'Arvexis Avatar' : `${userName}'s avatar`}
        style={{
          width: isDefault ? size * 0.65 : '100%',
          height: isDefault ? size * 0.65 : '100%',
          objectFit: isDefault ? 'contain' : 'cover',
        }}
      />
    </div>
  );
};

// =============================================================================
// WORDMARK (Horizontal: Symbol + ARVEXIS text)
// =============================================================================

/**
 * ArvexisWordmark - Symbol with "ARVEXIS" text beside it
 * For horizontal layouts like headers where full lockup doesn't fit
 */
export const ArvexisWordmark = ({ 
  symbolSize = 32, 
  className = '' 
}) => (
  <div 
    className={`arvexis-wordmark ${className}`}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}
    data-testid="arvexis-wordmark"
  >
    <ArvexisSymbol size={symbolSize} />
    <span 
      style={{ 
        color: 'var(--color-gold-primary, #D4AF37)',
        fontFamily: 'var(--font-display, "Cormorant Garamond", Georgia, serif)',
        fontSize: Math.max(16, symbolSize * 0.6),
        fontWeight: 500,
        letterSpacing: '0.3em',
        textTransform: 'uppercase'
      }}
    >
      ARVEXIS
    </span>
  </div>
);

// =============================================================================
// BREATHING SYMBOL (For loading states)
// =============================================================================

/**
 * ArvexisSymbolBreathing - Animated symbol for loading/processing states
 * Use instead of generic spinners for consistent branding
 */
export const ArvexisSymbolBreathing = ({ 
  size = SYMBOL_SIZES.XL, 
  className = '' 
}) => (
  <div 
    className={`arvexis-symbol-breathing-container ${className}`}
    style={{
      filter: 'drop-shadow(0 0 18px rgba(212, 175, 55, 0.35))'
    }}
  >
    <ArvexisSymbol size={size} breathing glow />
  </div>
);

// =============================================================================
// LOCKUP BREATHING (For premium loading states with full branding)
// =============================================================================

/**
 * ArvexisLockupBreathing - Animated full lockup for major loading states
 * Use for: Splash screen, Reflection loading, AI processing
 */
export const ArvexisLockupBreathing = ({ 
  width = LOCKUP_SIZES.LG, 
  className = '' 
}) => (
  <div 
    className={`arvexis-lockup-breathing-container ${className}`}
    style={{
      filter: 'drop-shadow(0 0 24px rgba(212, 175, 55, 0.25))',
      animation: 'arvexis-breathe 6s ease-in-out infinite'
    }}
  >
    <ArvexisLockup width={width} />
  </div>
);

// =============================================================================
// BACKWARD COMPATIBILITY - Legacy Exports
// =============================================================================

// Alias for existing code that imports ArvexisLogo
const ArvexisLogo = ArvexisSymbol;

// Legacy exports for backwards compatibility
export const ArvexisLogoFull = ({ logoSize = 52, className = '' }) => (
  <ArvexisLockup width={logoSize * 3.5} className={className} />
);

export const ArvexisLogoBreathing = ({ size = 80, className = '' }) => (
  <ArvexisSymbolBreathing size={size} className={className} />
);

export const LOGO_SIZES = SYMBOL_SIZES;

export default ArvexisLogo;
